import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { FullscreenLayout } from '@/components/layout/FullscreenLayout';
import { ShowroomConfiguratorProvider } from './ShowroomConfiguratorProvider';
import { ShowroomConfigurator } from './ShowroomConfigurator';
import { ShowroomI18nProvider } from './ShowroomI18nProvider';
import { IMMERSIVE_STYLE_VARS } from '@/components/OptionConfigurator/immersiveStyleVars';
import { ShowroomActionsProvider, ShowroomActionParams } from '@/components/OptionConfigurator/useShowroomActions';

// Public showroom entry point — mounted at /showroom/:projectSlug/:unitId/:hash, accessible
// without authentication. Designed to work both as a standalone page and embedded as an
// <iframe>. When in an iframe, the page reports its content height to the parent via
// postMessage so the parent can size the iframe without a scrollbar-in-scrollbar situation.
//
// Optional URL params:
//   ?locale=nb            — initial language
//   ?deadlineId=123       — pre-select a deadline
//   ?close_label=…        — show a topbar close button with this label (see useShowroomActions)
//   ?complete_label=…     — show a topbar complete button with this label
//   ?close_url=… / ?complete_url=…  — navigate the top window on click instead of emitting an event
//                                     (default: postMessage 'showroom-close' / 'showroom-complete' to the host)
export const ShowroomPage: React.FC = () => {
    const { projectSlug, unitId: unitIdRaw, hash } = useParams<{ projectSlug: string; unitId: string; hash: string }>();
    const unitId = Number(unitIdRaw);
    const location = useLocation();
    const match = useRouteMatch();
    const rootRef = useRef<HTMLDivElement | null>(null);

    const query = new URLSearchParams(location.search);
    const initialDeadlineId = (() => {
        const v = query.get('deadlineId');
        return v ? Number(v) : undefined;
    })();
    const requestedLocale = query.get('locale');

    // Capture the close/complete config from the entry URL once. The showroom redirects to a
    // query-less URL right after load (buildConfiguratorUrl), so this must not be re-read from
    // `location.search` on later renders — hence the one-time useState initializer.
    const [showroomActions] = useState<ShowroomActionParams>(() => {
        const q = new URLSearchParams(location.search);
        return {
            closeLabel: q.get('close_label') || undefined,
            completeLabel: q.get('complete_label') || undefined,
            closeUrl: q.get('close_url') || undefined,
            completeUrl: q.get('complete_url') || undefined,
        };
    });

    // Iframe height sync: when this page is embedded, the parent listens for
    // 'showroom-resize' messages and resizes the iframe accordingly.
    useEffect(() => {
        if (typeof window === 'undefined' || !rootRef.current) return;
        const inIframe = window.self !== window.top;
        if (!inIframe) return;

        const post = (height: number) => {
            window.parent?.postMessage({ type: 'showroom-resize', height }, '*');
        };

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) post(Math.ceil(entry.contentRect.height));
        });
        observer.observe(rootRef.current);
        // initial post in case ResizeObserver doesn't fire immediately
        post(rootRef.current.getBoundingClientRect().height);
        return () => observer.disconnect();
    }, []);

    const baseUrl = match.url; // e.g. /showroom/<slug>
    const basePath = match.path; // e.g. /showroom/:slug

    return (
        // The shared layout hides the global header, suppresses body scroll, and injects the immersive
        // CSS variables. No `overlay` here: the showroom is a top-level route with no sibling chrome to
        // cover, so it renders inline rather than portalling to <body>.
        //
        // Lock the widget to the viewport height. `.fit-height` (height:0; flex-grow:1) only fills
        // when its parent is a flex column with a definite height, but the showroom mounts directly
        // under `#root` (min-height:100%, a block) — so without an explicit height the flex chain
        // can't constrain and the whole page grows to content height (page scrollbar). Capping here
        // keeps the only scroll region the options panel (its own overflow-y: auto).
        <FullscreenLayout
            ref={rootRef}
            styleVars={IMMERSIVE_STYLE_VARS}
            className="fit-height"
            style={{ width: '100%', height: '100dvh', overflow: 'hidden' }}>
            <ShowroomI18nProvider requestedLocale={requestedLocale}>
                <ShowroomConfiguratorProvider
                    projectSlug={projectSlug}
                    unitId={unitId}
                    hash={hash}
                    basePath={basePath}
                    baseUrl={baseUrl}
                    initialDeadlineId={initialDeadlineId}>
                    <ShowroomActionsProvider value={showroomActions}>
                        <ShowroomConfigurator basePath={basePath} />
                    </ShowroomActionsProvider>
                </ShowroomConfiguratorProvider>
            </ShowroomI18nProvider>
        </FullscreenLayout>
    );
};
